import { CancelToken } from 'axios';
import { useCallback, useEffect, useState, useRef } from 'react';

import { usePrevious } from '@hivebrite/react-hooks';

import { CANCEL_FETCH_MESSAGE } from 'hb-react/shared/constants/cancelFetch';

import { HTTP_STATUS_CODE_403 } from '../constants/httpStatusCode';
import { useTopicsIndexContext } from '../contexts/TopicsIndexContext';
import fetchTopics from '../requests/fetchTopics';
import getNbItemsPerPage from '../utils/getNbItemsPerPage';
import removeDuplicateTopics from '../utils/removeDuplicateTopics';

const useTopics = ({ params, isAutomaticallyFetched = true }) => {
  const { itemsPerRow, setUserAuthorizationInfo } = useTopicsIndexContext();

  const [page, setPage] = useState(1);
  const [topics, setTopics] = useState([]);
  const [totalTopics, setTotalTopics] = useState(0);
  const [isFetchingTopics, setIsFetchingTopics] = useState(false);

  const totalTopicsRef = useRef(0);
  const cancelTokenRef = useRef(null);

  const prevPage = usePrevious(page);

  const perPage = getNbItemsPerPage(itemsPerRow);

  const handleFetchTopics = useCallback(
    async ({ resetTopics, ...otherParams }) => {
      if (isFetchingTopics && cancelTokenRef.current) {
        cancelTokenRef.current.cancel(CANCEL_FETCH_MESSAGE);
      }
      cancelTokenRef.current = CancelToken.source();

      setIsFetchingTopics(true);

      const response = await fetchTopics({
        cancelToken: cancelTokenRef?.current,
        per_page: perPage,
        v2: true,
        ...params,
        ...otherParams,
      });

      if (response?.response?.status === HTTP_STATUS_CODE_403) {
        setUserAuthorizationInfo({ isAuthorized: false, error: response });
      } else {
        setUserAuthorizationInfo({ isAuthorized: true });

        if (response?.topics) {
          // We overwrite the topics list if:
          // - it is requested explicitly by resetTopics (eg. for filtered groups)
          // - an item has been removed and we reload an existing age (use case: click on Join when follow-up-features-group-index is OFF)
          if (resetTopics || response.total !== totalTopicsRef?.current) {
            setTopics(response.topics);
          } else {
            // otherwise it can mean two things:
            // - we are loading a new page of data (use case: click on "load more" or the right arrow button)
            // - we are updating a single page of data (use case: click on Join when follow-up-features-group-index is ON)
            setTopics((prevState) =>
              removeDuplicateTopics([...prevState, ...response.topics]),
            );
          }
          setTotalTopics(response.total);
          totalTopicsRef.current = response.total;
          setIsFetchingTopics(false);
        }
      }
    },
    [isFetchingTopics],
  );

  const updateTopicApproval = useCallback(
    (topicId, approvalId, approvalCreatedAt = undefined) => {
      const updatedTopics = topics.map((topic) => {
        if (topic.id === topicId) {
          topic.approval_id = approvalId;

          // To update the label to "Joined"
          if (approvalCreatedAt) {
            topic.joined_at = approvalCreatedAt;
          }
        }

        return topic;
      });

      setTopics(updatedTopics);
    },
    [topics],
  );

  useEffect(() => {
    if (isAutomaticallyFetched && prevPage !== page) {
      handleFetchTopics({ page });
    }
  }, [page]);

  return {
    page,
    topics,
    totalTopics,
    isFetchingTopics,
    setPage,
    handleFetchTopics,
    updateTopicApproval,
  };
};

export default useTopics;
